import type { PermissionKey } from '@classroom-ai/types';
import type { NavSectionProps } from '@classroom-ai/ui/components/nav-section';

import { uiConfig } from '@classroom-ai/ui';
import { SvgColor } from '@classroom-ai/ui/components/svg-color';

import { paths } from 'src/routes/paths';

// ----------------------------------------------------------------------

const icon = (name: string) => (
  <SvgColor src={`${uiConfig.assetsDir}/assets/icons/navbar/${name}.svg`} />
);

const ICONS = {
  dashboard:  icon('ic-dashboard'),
  analytics:  icon('ic-analytics'),
  calendar:   icon('ic-calendar'),
  users:      icon('ic-user'),
  curriculum: icon('ic-course'),
  standards:  icon('ic-course'),
  reviews:    icon('ic-analytics'),
  lessonPlans: icon('ic-file'),
  district:   icon('ic-banking'),
  department: icon('ic-kanban'),
};

// ----------------------------------------------------------------------

/** A minimal permission probe — `{ has }` (not a Set) so call sites don't re-implement the
 *  ED360_STAFF bypass. Threaded through for the upcoming per-item nav gating (epic #143). */
export type NavPermissions = { has: (key: PermissionKey) => boolean };

// Role-aware nav. The shared layout's built-in `allowedRoles` gating reads a mocked
// user, so it can't see the real role — we filter here instead, where the auth claim is
// available. The nav is split into **Teaching** (class-scoped) and **Course** (course-scoped
// authoring). DISTRICT_ADMIN sees only the district dashboard (class-scoped items are
// meaningless without a class); ED360_STAFF sees both; SCHOOL_STAFF sees Teaching + Course.
//
// `perms` is threaded but unread in this slice — no nav item is permission-gated yet (Teacher
// sees everything; gating is Phase 4). It's wired now because the expensive part was the
// ClassProvider split that lets this run inside the provider, not the eventual `if`. Epic #143.
export function getNavData(
  role?: string,
  perms?: NavPermissions,
  opts: { showDepartment?: boolean; dashboardBadge?: React.ReactNode } = {}
): NavSectionProps['data'] {
  const isDistrictAdmin = role === 'DISTRICT_ADMIN';
  const canSeeDistrict = role === 'DISTRICT_ADMIN' || role === 'ED360_STAFF';

  const DASHBOARD = { title: 'Dashboard', path: paths.dashboard.root, icon: ICONS.dashboard, info: opts.dashboardBadge };
  const data: NavSectionProps['data'] = [];

  // District admins keep a standalone Dashboard (they have no Teaching section to hold it).
  if (isDistrictAdmin) {
    data.push({ items: [DASHBOARD] });
  } else {
    data.push({
      subheader: 'Teaching',
      items: [
        DASHBOARD,
        { title: 'Class overview', path: paths.dashboard.classOverview, icon: ICONS.dashboard },
        { title: 'Sessions', path: paths.dashboard.sessions, icon: ICONS.analytics },
        // deepMatch: stays highlighted on a student's Sessions page (/dashboard/class/students/…).
        { title: 'Class Roster', path: paths.dashboard.class, icon: ICONS.users, deepMatch: true },
        { title: 'Reviews', path: paths.dashboard.reviews, icon: ICONS.reviews },
        { title: 'Calendar', path: paths.dashboard.calendar, icon: ICONS.calendar },
      ],
    });
  }

  // Department / Team Lead (epic #566): only for staff with Lead access — a course they manage,
  // a teacher they oversee, or a class they can view (department.myScope).
  if (!isDistrictAdmin && opts.showDepartment) {
    data.push({
      subheader: 'Leading',
      items: [{ title: 'Department', path: paths.dashboard.department, icon: ICONS.department }],
    });
  }

  if (canSeeDistrict) {
    data.push({
      subheader: 'District',
      items: [
        { title: 'District', path: paths.dashboard.district.root, icon: ICONS.district },
      ],
    });
  }

  if (!isDistrictAdmin) {
    data.push({
      subheader: 'Course',
      items: [
        { title: 'Curriculum', path: paths.dashboard.curriculum, icon: ICONS.curriculum },
        { title: 'Lesson Plans', path: paths.dashboard.lessonPlans.root, icon: ICONS.lessonPlans },
        { title: 'Standards', path: paths.dashboard.standards, icon: ICONS.standards },
      ],
    });
  }

  return data;
}
